import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// ─────────────────────────────────────────────────────────────────────────
// ReportsConfigView — the "Reports" tab content inside ReleaseManagement.jsx.
//
// SCOPE (explicitly agreed): this configuration belongs ONLY to the Cost
// Control Report. It is NOT a generic reporting configuration engine —
// every future report (Executive Report, Cash Flow, etc.) will get its
// own independent configuration screen and table. Nothing here is
// designed to be reused by another report type.
//
// This component owns ONLY plain display/metadata fields (logo, cover
// page toggle, client/consultant names, website, footer, confidential
// label, print-date/generated-by toggles) — zero financial data, zero
// calculation. exportCostControlPdf.js reads this config at export time
// and passes it straight through to buildCostControlPdfModel(), which
// already accepts these fields.
export default function ReportsConfigView({ projectId }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState(null)

  const [logoUrl, setLogoUrl] = useState('')
  const [showCoverPage, setShowCoverPage] = useState(true)
  const [clientName, setClientName] = useState('')
  const [consultantName, setConsultantName] = useState('')
  const [orgWebsite, setOrgWebsite] = useState('')
  const [footerText, setFooterText] = useState('')
  const [confidentialLabel, setConfidentialLabel] = useState('Strictly Confidential — Top Management Only')
  const [showPrintDate, setShowPrintDate] = useState(true)
  const [showGeneratedBy, setShowGeneratedBy] = useState(true)

  const loadConfig = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    const { data } = await supabase
      .from('cost_control_report_config')
      .select('*')
      .eq('project_id', projectId)
      .maybeSingle()
    if (data) {
      setLogoUrl(data.logo_url || '')
      setShowCoverPage(data.show_cover_page ?? true)
      setClientName(data.client_name || '')
      setConsultantName(data.consultant_name || '')
      setOrgWebsite(data.org_website || '')
      setFooterText(data.footer_text || '')
      setConfidentialLabel(data.confidential_label || 'Strictly Confidential — Top Management Only')
      setShowPrintDate(data.show_print_date ?? true)
      setShowGeneratedBy(data.show_generated_by ?? true)
    }
    setLoading(false)
  }, [projectId])

  useEffect(() => { loadConfig() }, [loadConfig])

  async function handleSave() {
    if (!projectId) return
    setSaving(true)
    setError(null)
    try {
      const { error: upsertError } = await supabase
        .from('cost_control_report_config')
        .upsert({
          project_id: projectId,
          logo_url: logoUrl || null,
          show_cover_page: showCoverPage,
          client_name: clientName || null,
          consultant_name: consultantName || null,
          org_website: orgWebsite || null,
          footer_text: footerText || null,
          confidential_label: confidentialLabel || null,
          show_print_date: showPrintDate,
          show_generated_by: showGeneratedBy,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'project_id' })
      if (upsertError) throw upsertError
      setSavedAt(new Date())
    } catch (e) {
      console.error('Failed to save Cost Control Report config:', e)
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#6B7280', fontSize: 13 }}>
        Loading report configuration…
      </div>
    )
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
      <div style={{ maxWidth: 640 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#0F1017', marginBottom: 4 }}>Cost Control Report Configuration</div>
          <div style={{ fontSize: 11.5, color: '#6B7280', lineHeight: 1.5 }}>
            These settings apply only to the Cost Control Report PDF (both the full report and Print Screen exports).
            Every other report type will have its own independent configuration.
          </div>
        </div>

        <Field label="Company Logo URL">
          <input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://…"
            style={inputStyle} />
          <div style={hintStyle}>Leave blank to use the organization's default logo.</div>
        </Field>

        <ToggleField label="Show Cover Page" checked={showCoverPage} onChange={setShowCoverPage} />

        <Field label="Client Name">
          <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="e.g. Red Sea Global"
            style={inputStyle} />
        </Field>

        <Field label="Consultant Name">
          <input value={consultantName} onChange={e => setConsultantName(e.target.value)} placeholder="e.g. Dar Al-Handasah"
            style={inputStyle} />
        </Field>

        <Field label="Company Website">
          <input value={orgWebsite} onChange={e => setOrgWebsite(e.target.value)} placeholder="https://…"
            style={inputStyle} />
        </Field>

        <Field label="Footer Text">
          <input value={footerText} onChange={e => setFooterText(e.target.value)} placeholder="e.g. CostSpace Pro · Cost Management ERP"
            style={inputStyle} />
        </Field>

        <Field label="Confidential Label">
          <input value={confidentialLabel} onChange={e => setConfidentialLabel(e.target.value)}
            style={inputStyle} />
        </Field>

        <ToggleField label="Show Print Date" checked={showPrintDate} onChange={setShowPrintDate} />
        <ToggleField label="Show Generated By" checked={showGeneratedBy} onChange={setShowGeneratedBy} />

        {error && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 10 }}>{error}</p>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '9px 20px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
          {savedAt && !saving && (
            <span style={{ fontSize: 12, color: '#1A7A4A' }}>Saved at {savedAt.toLocaleTimeString()}</span>
          )}
        </div>
      </div>
    </div>
  )
}

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }
const hintStyle = { fontSize: 10.5, color: '#9CA3AF', marginTop: 4 }

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  )
}

function ToggleField({ label, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <button onClick={() => onChange(!checked)}
        style={{
          width: 38, height: 22, borderRadius: 999, border: 'none', cursor: 'pointer', position: 'relative',
          background: checked ? '#1A7A4A' : '#D1D5DB', transition: 'background .15s', flexShrink: 0,
        }}>
        <span style={{
          position: 'absolute', top: 2, left: checked ? 18 : 2, width: 18, height: 18, borderRadius: '50%',
          background: '#fff', transition: 'left .15s',
        }} />
      </button>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: '#374151', cursor: 'pointer' }} onClick={() => onChange(!checked)}>
        {label}
      </label>
    </div>
  )
}
